
import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Camera, Smartphone, Video, Zap, Star, Quote, Trophy, MapPin, CheckCircle, Sparkles, TrendingUp, Users, ArrowRight, MousePointerClick } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { GlassCard } from '../components/ui/GlassCard';
import { PRODUCTS } from '../constants';
import { CaseStudyModal, CaseStudy } from '../components/CaseStudyModal';

const niches = ["Weddings", "Corporate Events", "Brand Activations"];

const CLIENTS = [
  "Spark NZ", "Air New Zealand", "Xero", "Fonterra", "Vodafone", "Fisher & Paykel", "Datacom", "Trade Me", "ANZ Bank", "SkyCity"
];

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1561489413-985b06da5bee?q=80&w=2670&auto=format&fit=crop", // Concert/Crowd
  "https://images.unsplash.com/photo-1519671482538-eb62635ca436?q=80&w=2670&auto=format&fit=crop", // Wedding/Outdoor
  "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=2670&auto=format&fit=crop"  // Party/Lights
];

const REVIEWS = [
  {
    name: "Sarah Mitchell",
    role: "Event Coordinator, Spark",
    content: "The 360 booth was the absolute highlight of our end-of-year party. The team was professional, the setup looked incredibly premium, and the branding was spot on.",
    rating: 5
  },
  {
    name: "David Tui",
    role: "Marketing Director, Xero",
    content: "We used BrandPix for our product launch. The AI background removal is magic – engagement on social media went through the roof. Highly recommended.",
    rating: 5
  },
  {
    name: "Jessica & Tom",
    role: "Wedding, Mudbrick Vineyard",
    content: "I was worried about lighting in our dark reception venue, but the Glam Booth looked incredible. Everyone looked like movie stars! Best money we spent.",
    rating: 5
  },
  {
    name: "TechConf NZ",
    role: "Aotea Centre",
    content: "BrandPix is a game changer. We captured over 800 leads in 4 hours because everyone wanted the AI photo. The analytics report afterwards was the cherry on top.",
    rating: 5
  },
  {
    name: "Ruby S.",
    role: "21st Birthday",
    content: "Hired the Pro Roam for my 21st. The photos were on everyone's Instagram story instantly. The operator was super fun and got everyone involved.",
    rating: 5
  },
  {
    name: "James & Oliver",
    role: "Wedding, Kauri Bay Boomrock",
    content: "The Audio Guestbook was such a hit. Listening to the voicemails the next day was emotional and hilarious. A keepsake we will treasure forever.",
    rating: 5
  }
];

const SOCIAL_PROOFS = [
  { text: "500+ Events Delivered Nationwide", icon: <Trophy size={16} /> },
  { text: "Average 4.9/5 Star Rating", icon: <Star size={16} /> },
  { text: "100% Kiwi Owned & Operated", icon: <MapPin size={16} /> },
  { text: "Trusted by NZ's Top Brands", icon: <CheckCircle size={16} /> }
];

const EXPANDED_CASE_STUDIES: CaseStudy[] = [
  {
    id: '1',
    client: "Vodafone Music Awards",
    industry: "Entertainment",
    image: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&q=80&w=800",
    desc: "Capturing red carpet moments in real-time.",
    challenge: "The VMAs needed a way to amplify social reach during the red carpet arrivals. Traditional static photos were too slow to share and lacked the 'glam' factor required for a televised music event.",
    solution: "We deployed 4x synchronized 360 Video Booths with instant 5G sharing stations. Guests received a branded, music-synced video within 15 seconds of stepping off the platform.",
    stats: [
      { label: "Impressions", value: "3.2M", icon: <TrendingUp size={16} /> },
      { label: "Videos Shared", value: "1,400+", icon: <Video size={16} /> },
      { label: "Emails Captured", value: "850", icon: <Users size={16} /> }
    ],
    testimonial: {
      text: "The speed of delivery was incredible. It was trending on Twitter before the show even started.",
      author: "Event Director",
      role: "Vodafone Music Awards"
    }
  },
  {
    id: '2',
    client: "Mecca Beauty Launch",
    industry: "Retail / Cosmetics",
    image: "https://images.unsplash.com/photo-1522335789203-abd7fe01f169?auto=format&fit=crop&q=80&w=800",
    desc: "AI transport to Paris without a green screen.",
    challenge: "Mecca wanted to launch a new Parisian-themed fragrance line in a small retail footprint. They needed an immersive photo experience but had no room for a physical green screen backdrop.",
    solution: "We utilized BrandPix AI technology. The kiosk automatically removed the busy store background and replaced it with a twilight Eiffel Tower scene. The result was seamless and required only 1m x 1m of floor space.",
    stats: [
      { label: "UGC Content", value: "450%", icon: <Camera size={16} /> },
      { label: "Avg Dwell Time", value: "4m 20s", icon: <CheckCircle size={16} /> },
      { label: "Sales Uplift", value: "18%", icon: <TrendingUp size={16} /> }
    ]
  },
  {
    id: '3',
    client: "Spark 5G Roadshow",
    industry: "Technology",
    image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80&w=800",
    desc: "Demonstrating network speed via instant uploads.",
    challenge: "Spark needed a tangible way to demonstrate the upload speeds of their new 5G network to the general public at pop-up locations across the country.",
    solution: "We created a 'Hologram' photo experience that processed 4K video files in the cloud. The massive files were transferred instantly via the 5G modems, serving as a live demo of the network's capability.",
    stats: [
      { label: "Cities Visited", value: "12", icon: <MapPin size={16} /> },
      { label: "Demos Run", value: "5,000+", icon: <CheckCircle size={16} /> },
      { label: "Brand Recall", value: "High", icon: <Star size={16} /> }
    ],
    testimonial: {
      text: "QuikPix turned a boring technical stat into a fun, shareable moment.",
      author: "Marketing Lead",
      role: "Spark NZ"
    }
  },
  {
    id: '4',
    client: "The Wilder Wedding",
    industry: "Private Event",
    image: "https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?auto=format&fit=crop&q=80&w=800",
    desc: "A timeless Black & White Glam experience.",
    challenge: "The couple wanted a photo booth but hated the 'cheesy' props and colorful strips. They wanted something that looked like a Vanity Fair photoshoot.",
    solution: "We installed the 'Kardashian' Glam Booth. High-key studio lighting, black and white smoothing filter, and a simple white backdrop. No props, just pure elegance.",
    stats: [
      { label: "Photos Taken", value: "600+", icon: <Camera size={16} /> },
      { label: "Guestbook", value: "Full", icon: <Users size={16} /> },
      { label: "Vibe", value: "10/10", icon: <Star size={16} /> }
    ]
  },
  {
    id: '5',
    client: "Air NZ Annual Gala",
    industry: "Corporate",
    image: "https://images.unsplash.com/photo-1569930784237-d5508f629328?auto=format&fit=crop&q=80&w=800",
    desc: "Roaming photography for a 1,000 guest event.",
    challenge: "With a massive venue and 1,000 guests, a stationary booth would form lines and miss the action at the tables.",
    solution: "We deployed 3x Pro Roam operators. They moved through the crowd capturing groups at tables and on the dance floor, instantly beaming photos to the venue's large LED screens.",
    stats: [
      { label: "Screen Time", value: "4hrs", icon: <Video size={16} /> },
      { label: "Interactions", value: "1.2k", icon: <Users size={16} /> },
      { label: "Happiness", value: "Max", icon: <Sparkles size={16} /> }
    ]
  }
];

export const Home = ({ onOpenQuote }: { onOpenQuote: () => void }) => {
  const [nicheIndex, setNicheIndex] = useState(0);
  const [proofIndex, setProofIndex] = useState(0);
  const [heroImageIndex, setHeroImageIndex] = useState(0);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<CaseStudy | null>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Scroll to section handling
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  useEffect(() => {
    // Cycle Niches
    const nicheInterval = setInterval(() => {
      setNicheIndex((prev) => (prev + 1) % niches.length);
    }, 3000);

    // Cycle Social Proof
    const proofInterval = setInterval(() => {
      setProofIndex((prev) => (prev + 1) % SOCIAL_PROOFS.length);
    }, 4000);

    // Cycle Hero Images
    const heroInterval = setInterval(() => {
      setHeroImageIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);

    const handleScroll = () => {
      if (parallaxRef.current) {
        const scrolled = window.scrollY;
        // Move background down at 40% of scroll speed to create depth
        parallaxRef.current.style.transform = `translateY(${scrolled * 0.4}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      clearInterval(nicheInterval);
      clearInterval(proofInterval);
      clearInterval(heroInterval);
      window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Overlay - Wrapped for Parallax */}
        <div ref={parallaxRef} className="absolute inset-0 z-0 will-change-transform">
           {HERO_IMAGES.map((img, index) => (
             <img 
              key={img}
              src={img} 
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out animate-zoom-slow ${
                index === heroImageIndex ? 'opacity-30' : 'opacity-0'
              }`}
              alt={`Event Background ${index + 1}`}
            />
           ))}
        </div>
        
        {/* Static Overlay Gradients */}
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-slate-950 via-slate-950/80 to-slate-950 pointer-events-none"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-neonBlue/20 rounded-full blur-[120px] animate-pulse-slow z-0 pointer-events-none"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <div className="inline-block px-4 py-1.5 mb-6 rounded-full border border-brand-neonPink/50 bg-brand-neonPink/10 backdrop-blur-md animate-fade-in-up">
            <span className="text-brand-neonPink text-xs font-bold tracking-widest uppercase">New in NZ: AI Background Removal</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-display font-bold text-white mb-6 leading-tight animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            Next-Gen Photo Booths <br />
            <span className="text-2xl md:text-4xl text-slate-400 font-sans font-light block my-2">for</span>
            
            <div className="h-[1.2em] relative flex justify-center overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.span 
                    key={niches[nicheIndex]}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-brand-neonBlue to-brand-neonPink pb-2"
                  >
                    {niches[nicheIndex]}
                  </motion.span>
                </AnimatePresence>
            </div>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Elevate your event with QuikPix. We combine premium hardware with cutting-edge software to deliver shareable moments that amplify your brand.
          </p>

          <div className="flex flex-col items-center animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button size="lg" onClick={onOpenQuote}>
                Get a Quote
                <ChevronRight size={20} />
              </Button>
              <Button variant="outline" size="lg" onClick={() => document.getElementById('products')?.scrollIntoView({behavior: 'smooth'})}>
                View Products
              </Button>
            </div>

            {/* Cycling Social Proof Widget */}
            <div className="h-10 flex items-center justify-center gap-3 px-6 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
               <span className="text-brand-neonBlue animate-pulse">{SOCIAL_PROOFS[proofIndex].icon}</span>
               <span key={proofIndex} className="text-slate-300 text-sm font-medium animate-fade-in-up">
                 {SOCIAL_PROOFS[proofIndex].text}
               </span>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By / Logo Carousel */}
      <section className="py-12 border-y border-white/5 bg-slate-900/50 overflow-hidden relative z-20">
        <div className="max-w-7xl mx-auto px-4 mb-8 text-center">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-[0.2em]">Trusted by New Zealand's Best</p>
        </div>
        
        <div className="relative flex overflow-x-hidden group">
          <div className="flex animate-marquee whitespace-nowrap">
            {/* First Set */}
            <div className="flex items-center gap-16 px-8">
              {CLIENTS.map((client, i) => (
                <span key={`1-${i}`} className="text-2xl md:text-3xl font-display font-bold text-slate-700 uppercase hover:text-white transition-colors duration-300 cursor-default select-none">
                  {client}
                </span>
              ))}
            </div>
            {/* Duplicate Set for Loop */}
            <div className="flex items-center gap-16 px-8">
              {CLIENTS.map((client, i) => (
                <span key={`2-${i}`} className="text-2xl md:text-3xl font-display font-bold text-slate-700 uppercase hover:text-white transition-colors duration-300 cursor-default select-none">
                  {client}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MOVED UP: Feature Section (Flagship) */}
      <section className="py-24 relative bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">Flagship Experiences</h2>
            <p className="text-slate-400">Technology that stops the scroll. Choose your weapon.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link to="/product/360-video">
              <GlassCard hoverEffect className="relative overflow-hidden group h-full">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Video size={100} className="text-brand-neonBlue" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">360x Video Booth</h3>
                <p className="text-slate-400 mb-4">High-definition slow motion video with custom overlays and music. The VIP treatment.</p>
                <div className="w-full h-48 rounded-lg overflow-hidden mb-4">
                   <img src="https://images.unsplash.com/photo-1533174072545-e8d4aa97edf9?auto=format&fit=crop&q=80&w=800" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt="360 Booth"/>
                </div>
                <span className="text-brand-neonBlue font-semibold text-sm flex items-center gap-1">Learn More <ChevronRight size={16}/></span>
              </GlassCard>
            </Link>

            <Link to="/product/brandpix">
              <GlassCard hoverEffect className="relative overflow-hidden group border-brand-neonPink/30 h-full">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Zap size={100} className="text-brand-neonPink" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">AI Background Removal</h3>
                <p className="text-slate-400 mb-4">No green screen needed. Transport your guests anywhere instantly with AI precision.</p>
                <div className="w-full h-48 rounded-lg overflow-hidden mb-4">
                   <img src="https://images.unsplash.com/photo-1626785774573-4b79931b2993?auto=format&fit=crop&q=80&w=800" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt="AI Photo"/>
                </div>
                <span className="text-brand-neonPink font-semibold text-sm flex items-center gap-1">Learn More <ChevronRight size={16}/></span>
              </GlassCard>
            </Link>

            <Link to="/product/brandpix">
              <GlassCard hoverEffect className="relative overflow-hidden group h-full">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Smartphone size={100} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">BrandPix App</h3>
                <p className="text-slate-400 mb-4">Turn every guest smartphone into a branded content creation tool for your event.</p>
                 <div className="w-full h-48 rounded-lg overflow-hidden mb-4">
                   <img src="https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&q=80&w=800" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt="BrandPix App"/>
                </div>
                <span className="text-brand-neonBlue font-semibold text-sm flex items-center gap-1">Learn More <ChevronRight size={16}/></span>
              </GlassCard>
            </Link>
          </div>
        </div>
      </section>

      {/* NEW SECTION: Spotlight Testimonial (High Impact Social Proof) */}
      <section className="py-12 bg-gradient-to-r from-brand-neonBlue/5 via-slate-900 to-brand-neonPink/5 border-y border-white/5">
        <div className="max-w-5xl mx-auto px-4 text-center">
           <Quote size={40} className="text-brand-neonBlue mx-auto mb-6 opacity-50" />
           <p className="text-2xl md:text-3xl font-display font-bold text-white mb-6 leading-relaxed">
             "{REVIEWS[0].content}"
           </p>
           <div className="flex flex-col items-center">
             <div className="flex gap-1 mb-2">
                {[1,2,3,4,5].map(s => <Star key={s} size={16} className="text-yellow-400 fill-yellow-400" />)}
             </div>
             <p className="text-white font-bold">{REVIEWS[0].name}</p>
             <p className="text-brand-neonBlue text-sm font-bold uppercase tracking-wider">{REVIEWS[0].role}</p>
           </div>
        </div>
      </section>

      {/* MOVED DOWN: EXPANDED CASE STUDIES SECTION */}
      <section id="case-studies" className="py-24 bg-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12">
            <div>
              <span className="text-brand-neonBlue font-bold tracking-widest text-xs uppercase mb-2 block">Proven ROI</span>
              <h2 className="text-3xl md:text-5xl font-display font-bold text-white mb-4">Results that matter.</h2>
              <p className="text-slate-400 max-w-2xl">
                Explore how we help brands capture data and create viral moments. Click on a case study to see the full breakdown.
              </p>
            </div>
            <div className="flex items-center gap-4 mt-6 md:mt-0">
               {/* Scroll Controls */}
               <button onClick={scrollLeft} className="p-3 rounded-full border border-white/10 hover:bg-white/10 text-white transition-colors hidden md:block">
                 <ArrowRight size={20} className="rotate-180" />
               </button>
               <button onClick={scrollRight} className="p-3 rounded-full border border-white/10 hover:bg-white/10 text-white transition-colors hidden md:block">
                 <ArrowRight size={20} />
               </button>
               <Link to="/mockup-studio">
                <Button variant="outline">
                  Visualize Your Brand <Sparkles size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Horizontal Scroll Container */}
          <div 
            ref={scrollContainerRef}
            className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-8 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {EXPANDED_CASE_STUDIES.map((study) => (
              <div 
                key={study.id} 
                onClick={() => setSelectedCaseStudy(study)}
                className="min-w-[85vw] md:min-w-[450px] snap-center cursor-pointer group"
              >
                <GlassCard className="p-0 overflow-hidden h-full hover:border-brand-neonBlue/50 transition-colors duration-300">
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={study.image} 
                      alt={study.client} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent"></div>
                    <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold uppercase text-white border border-white/10">
                      {study.industry}
                    </div>
                    
                    {/* Hover Hint */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-slate-950/40 backdrop-blur-[2px]">
                       <span className="flex items-center gap-2 text-white font-bold bg-brand-neonBlue/20 px-4 py-2 rounded-full border border-brand-neonBlue">
                         <MousePointerClick size={16} /> View Case Study
                       </span>
                    </div>

                    <div className="absolute bottom-6 left-6">
                      <h3 className="text-2xl font-bold text-white mb-1">{study.client}</h3>
                      <p className="text-sm text-slate-300 line-clamp-1">{study.desc}</p>
                    </div>
                  </div>
                  <div className="p-6 grid grid-cols-3 gap-4 border-t border-white/10 bg-slate-900/50">
                    {study.stats.map((stat, i) => (
                      <div key={i} className="text-center">
                        <div className="flex justify-center text-brand-neonBlue mb-2">{stat.icon}</div>
                        <div className="text-lg font-bold text-white">{stat.value}</div>
                        <div className="text-[9px] uppercase text-slate-500 font-bold tracking-wide">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </GlassCard>
              </div>
            ))}
            
            {/* "More" Card */}
            <div className="min-w-[200px] snap-center flex items-center justify-center">
              <div className="text-center p-8">
                <h3 className="text-white font-bold mb-2">Want to see more?</h3>
                <p className="text-slate-400 text-sm mb-4">We have 100+ successful events in our portfolio.</p>
                <Button variant="outline" size="sm" onClick={onOpenQuote}>Contact Us</Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CaseStudyModal 
        study={selectedCaseStudy} 
        isOpen={!!selectedCaseStudy} 
        onClose={() => setSelectedCaseStudy(null)}
        onQuote={onOpenQuote}
      />

      {/* LEAD GEN WIZARD TEASER */}
      <section className="py-20 bg-gradient-to-r from-brand-neonBlue/5 to-brand-neonPink/5 border-y border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-brand-neonBlue/10 rounded-full blur-[100px] -translate-y-1/2"></div>
          <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-brand-neonPink/10 rounded-full blur-[100px] -translate-y-1/2"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-brand-neonBlue text-xs font-bold uppercase tracking-wider">
            <Sparkles size={12} /> Not sure what to choose?
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-6">Find Your Perfect Photo Match</h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Take our 30-second interactive quiz. Tell us about your event style and budget, and our AI will recommend the perfect package (with bundle savings).
          </p>
          <Link to="/event-wizard">
             <Button size="lg" className="animate-pulse-slow">
               Launch Recommendation Engine <ChevronRight size={20} />
             </Button>
          </Link>
        </div>
      </section>

      {/* Full Product Grid */}
      <section id="products" className="py-24 bg-slate-950 relative">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-brand-neonBlue/20 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="flex justify-between items-end mb-12">
             <div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-white">Full Product Suite</h2>
              <p className="text-slate-400 mt-2">Everything you need for a world-class activation.</p>
             </div>
             <Button variant="outline" className="hidden md:flex">View Pricing</Button>
           </div>

           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
             {PRODUCTS.map((product) => (
               <Link to={`/product/${product.id}`} key={product.id} className="group relative rounded-xl overflow-hidden bg-slate-900 border border-white/5 hover:border-brand-neonBlue/50 transition-all duration-300">
                 <div className="aspect-video overflow-hidden">
                   <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                 </div>
                 <div className="p-6">
                   <div className="flex gap-2 mb-3">
                     {product.tags.map(tag => (
                       <span key={tag} className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-white/5 text-slate-300 border border-white/10">{tag}</span>
                     ))}
                   </div>
                   <h3 className="text-xl font-bold text-white mb-2">{product.name}</h3>
                   <p className="text-sm text-slate-400 mb-4">{product.description}</p>
                 </div>
               </Link>
             ))}
           </div>

           <div className="mt-12 text-center md:hidden">
              <Button variant="outline" className="w-full">View Pricing</Button>
           </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-900/30"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-brand-neonBlue/5 rounded-full blur-[120px]"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">Don't just take our word for it</h2>
            <p className="text-slate-400">See what our clients are saying.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {REVIEWS.map((review, i) => (
              <GlassCard key={i} className="relative flex flex-col h-full hover:border-brand-neonPink/40 transition-colors duration-300">
                <Quote size={40} className="text-brand-neonBlue/20 absolute top-4 right-4" />
                
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>

                <p className="text-slate-300 mb-6 flex-1 italic leading-relaxed">
                  "{review.content}"
                </p>

                <div className="mt-auto pt-4 border-t border-white/5">
                  <p className="text-white font-bold">{review.name}</p>
                  <p className="text-brand-neonBlue text-sm">{review.role}</p>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-neonBlue/10 to-brand-neonPink/10"></div>
        <div className="relative z-10 text-center px-4">
          <h2 className="text-4xl md:text-6xl font-display font-bold text-white mb-6">Ready to create magic?</h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">Join hundreds of top brands and happy couples across New Zealand.</p>
          <Button size="lg" variant="secondary" onClick={onOpenQuote}>Start Your Quote</Button>
        </div>
      </section>
    </div>
  );
}
